import type {
  EsignActivityConfig,
  EsignActivityPayload,
  EsignWizardDocument,
} from '@/types/esignature';

/**
 * Per-candidate overrides for `/esign_documents/{send,preview}`.
 *
 * Every field is optional and read by **presence**, not by value: a key that is
 * absent inherits the activity's configured value, and a key that is present
 * overrides it for this one envelope. That is why this is a diff and not a
 * serialisation of the whole form — sending an untouched field is not a no-op,
 * it pins the value this candidate gets even if the stage template later moves.
 *
 * `employer_signer_required` is deliberately absent and must stay that way. The
 * signing topology is a config decision: the backend ignores `company_signer_id`
 * on an activity configured without an employer signer, so an override cannot
 * introduce one.
 */
export interface EsignSendOverrides {
  email_subject?: string;
  email_body?: string;
  company_signer_id?: string | number;
  signing_order?: string;
  custom_message_per_recipient?: boolean;
  company_email_subject?: string;
  company_email_body?: string;
  cc_email_subject?: string;
  cc_email_body?: string;
  cc_user_ids?: number[];
  /** Full intended set: an existing document's id, or a newly uploaded File. */
  esign_documents?: (string | File)[];
}

/** `''` and `undefined`/`null` both mean "nothing here" — don't diff them apart. */
const sameText = (a: unknown, b: unknown): boolean => (a ?? '') === (b ?? '');

const sameIdList = (a: number[] = [], b: number[] = []): boolean =>
  a.length === b.length &&
  a.every((value, index) => String(value) === String(b[index]));

/**
 * Did the recruiter touch the document set?
 *
 * Any new upload counts. Otherwise the set is unchanged only if it is the same
 * saved ids in the same order — order matters, because it is the order the
 * signer sees them in.
 */
const documentsChanged = (
  documents: EsignWizardDocument[],
  saved: EsignActivityConfig['esign_documents'] = []
): boolean => {
  if (documents.some((document) => document.file instanceof File)) return true;

  const ids = documents.map((document) => String(document.id));
  const savedIds = saved.map((document) => String(document.id));
  return (
    ids.length !== savedIds.length ||
    ids.some((id, index) => id !== savedIds[index])
  );
};

const toDocumentEntries = (
  documents: EsignWizardDocument[]
): (string | File)[] =>
  documents.reduce<(string | File)[]>((entries, document) => {
    if (document.file instanceof File) return [...entries, document.file];
    if (document.id !== undefined && document.id !== null) {
      return [...entries, String(document.id)];
    }
    return entries;
  }, []);

/**
 * Diff the wizard's model against the activity it was seeded from, and keep only
 * what actually changed.
 *
 * This is what makes "edit for one candidate" safe. The drawer is seeded from
 * the stage activity, which every candidate on that stage shares — the earlier
 * implementation sent the whole payload to `/preview_adhoc`, which detached the
 * envelope from the activity entirely (wrong row, wrong grouping, no longer the
 * planned activity). Sending the *difference* to `/preview` keeps it the planned
 * activity and leaves the template — and therefore every other candidate on the
 * stage — untouched.
 */
export const buildEsignSendOverrides = (
  payload: EsignActivityPayload,
  config: EsignActivityConfig
): EsignSendOverrides => {
  const overrides: EsignSendOverrides = {};

  if (!sameText(payload.email_subject, config.email_subject)) {
    overrides.email_subject = payload.email_subject ?? '';
  }
  if (!sameText(payload.email_body, config.email_body)) {
    overrides.email_body = payload.email_body ?? '';
  }

  // Only meaningful when the activity already has an employer signer; the
  // backend ignores it otherwise, so there is no point sending it.
  if (
    config.employer_signer_required &&
    String(payload.company_signer_id ?? '') !==
      String(config.company_signer_id ?? config.company_signer?.id ?? '')
  ) {
    if (payload.company_signer_id !== undefined) {
      overrides.company_signer_id = payload.company_signer_id;
    }
  }

  if (
    config.employer_signer_required &&
    !sameText(payload.signing_order, config.signing_order)
  ) {
    overrides.signing_order = payload.signing_order;
  }

  if (
    Boolean(payload.custom_message_per_recipient) !==
    Boolean(config.custom_message_per_recipient)
  ) {
    overrides.custom_message_per_recipient = Boolean(
      payload.custom_message_per_recipient
    );
  }

  if (!sameText(payload.company_email_subject, config.company_email_subject)) {
    overrides.company_email_subject = payload.company_email_subject ?? '';
  }
  if (!sameText(payload.company_email_body, config.company_email_body)) {
    overrides.company_email_body = payload.company_email_body ?? '';
  }
  if (!sameText(payload.cc_email_subject, config.cc_email_subject)) {
    overrides.cc_email_subject = payload.cc_email_subject ?? '';
  }
  if (!sameText(payload.cc_email_body, config.cc_email_body)) {
    overrides.cc_email_body = payload.cc_email_body ?? '';
  }

  if (!sameIdList(payload.cc_user_ids, config.cc_user_ids)) {
    overrides.cc_user_ids = payload.cc_user_ids ?? [];
  }

  if (documentsChanged(payload.documents, config.esign_documents)) {
    overrides.esign_documents = toDocumentEntries(payload.documents);
  }

  return overrides;
};

export const hasEsignSendOverrides = (
  overrides?: EsignSendOverrides
): boolean => Boolean(overrides && Object.keys(overrides).length > 0);

const hasUploads = (overrides: EsignSendOverrides): boolean =>
  Boolean(overrides.esign_documents?.some((entry) => entry instanceof File));

/**
 * Serialise the overrides onto a request body.
 *
 * JSON whenever it can be: it is the only encoding that can express "the
 * recruiter removed the last CC recipient". `multipart/form-data` drops an empty
 * array in transit, so `cc_user_ids: []` would arrive as an absent key and read
 * as *untouched* — the same ambiguity `CC_CLEAR_SENTINEL_KEYS` works around on
 * the config endpoint. Multipart is therefore used only when there is a File to
 * carry, which is the one case JSON cannot handle.
 *
 * That leaves a real gap: clearing every CC recipient **and** uploading a new
 * document in the same request cannot be expressed. It is flagged for the
 * backend rather than papered over with a sentinel of our own invention.
 */
export const buildEsignSendBody = (
  base: Record<string, string | number>,
  overrides?: EsignSendOverrides
): Record<string, unknown> | FormData => {
  if (!hasEsignSendOverrides(overrides)) return base;
  const resolved = overrides as EsignSendOverrides;

  if (!hasUploads(resolved)) return { ...base, ...resolved };

  const formData = new FormData();
  Object.entries(base).forEach(([key, value]) => {
    formData.append(key, String(value));
  });

  const {
    esign_documents: documents,
    cc_user_ids: ccUserIds,
    ...scalars
  } = resolved;

  Object.entries(scalars).forEach(([key, value]) => {
    if (value === undefined) return;
    formData.append(key, String(value));
  });
  ccUserIds?.forEach((id) => formData.append('cc_user_ids[]', String(id)));
  documents?.forEach((entry) => formData.append('esign_documents[]', entry));

  return formData;
};
